import { useQuery, UseQueryResult } from "react-query";

import { getCurrentAuthApprovedHours } from "api/customers";

export interface InsuranceCodeRef {
  id: number;
  code: string;
  description: string | null;
}

export interface ApprovedHoursByCode {
  insuranceCode: InsuranceCodeRef;
  approvedUnits: number | null;
  approvedHours: string | null;
}

export interface CurrentAuthApprovedHoursAuth {
  id: number;
  authorizationCode: string | null;
  startDate: string | null;
  endDate: string | null;
  insurancePlanId: number | null;
}

interface UseGetCurrentAuthApprovedHoursReturn {
  currentAuth: CurrentAuthApprovedHoursAuth | null;
  approvedByCode: ApprovedHoursByCode[];
  currentAuthApprovedHoursLoading: boolean;
  currentAuthApprovedHoursFetching: boolean;
  refetchCurrentAuthApprovedHours: () => void;
}

const useGetCurrentAuthApprovedHours = (
  clientId?: number | string
): UseGetCurrentAuthApprovedHoursReturn &
  Omit<UseQueryResult, "data" | "isLoading" | "isFetching" | "refetch"> => {
  const { data, isLoading, isFetching, refetch, ...rest } = useQuery(
    ["currentAuthApprovedHours", clientId],
    () => getCurrentAuthApprovedHours(clientId!),
    {
      enabled: Boolean(clientId),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const body = data?.data as
    | { auth: CurrentAuthApprovedHoursAuth | null; approvedByCode: ApprovedHoursByCode[] }
    | undefined;

  return {
    currentAuth: body?.auth ?? null,
    approvedByCode: body?.approvedByCode ?? [],
    currentAuthApprovedHoursLoading: isLoading,
    currentAuthApprovedHoursFetching: isFetching,
    refetchCurrentAuthApprovedHours: refetch,
    ...rest,
  };
};

export default useGetCurrentAuthApprovedHours;
